/**
 * This file is part of the Valkyrie Robotics public website.
 * © 2025 Valkyrie Robotics, LLC. All rights reserved.
 */

import { motion } from "framer-motion";
import { Section } from "../components/Section";
import { SEO } from "../components/SEO";
import { FreeTrialSchedule } from "../components/FreeTrialSchedule";

export function IQPage() {
  return (
    <>
      <SEO
        title="VEX IQ Robotics Program"
        description="VEX IQ robotics coaching for ages 8-14 in Forsyth County, GA. Learn engineering, coding, and teamwork through hands-on robotics competitions. Perfect for elementary and middle school students."
        canonical="/iq"
        keywords={[
          "VEX IQ",
          "VEX IQ robotics",
          "elementary robotics",
          "middle school robotics",
          "robotics for kids",
          "VEX IQ coaching",
          "robotics education",
          "STEM robotics",
          "competitive robotics",
          "Forsyth County robotics",
        ]}
      />
      <div className="bg-neutral-900">
        {/* Hero Section */}
        <section className="relative overflow-hidden">
          <div className="mx-auto max-w-6xl px-4 py-24 text-gray-100">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="space-y-6">
                <motion.h1
                  className="text-4xl md:text-5xl font-bold tracking-tight text-white"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6 }}
                >
                  Competitive VEX IQ Robotics Coaching Program
                </motion.h1>
                <motion.p
                  className="text-xl text-brand-400 font-semibold"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                >
                  Hands-on engineering, coding, and competition coaching for
                  ages 8–14
                </motion.p>
                <motion.p
                  className="text-lg text-gray-300 leading-relaxed"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                >
                  If you're a parent looking to get your child involved in
                  robotics, you've probably come across something called VEX IQ.
                  But what exactly is it? And why are so many schools, coaches,
                  and STEM programs recommending it for young students?
                </motion.p>
                <motion.div
                  className="flex flex-col sm:flex-row items-center gap-4"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                >
                  <motion.button
                    data-cal-namespace="free-trial"
                    data-cal-link="aplakosh/free-trial"
                    data-cal-config='{"layout":"month_view"}'
                    className="w-full sm:w-auto rounded-lg bg-brand-600 text-neutral-900 font-semibold px-8 py-4 text-lg transition-all duration-150 hover:scale-105 hover:bg-brand-500 active:scale-95 shadow-lg"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Book Free Trial Session
                  </motion.button>
                  <FreeTrialSchedule />
                </motion.div>
              </div>
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="relative"
              >
                <div className="absolute inset-0 bg-gradient-to-br from-brand-500/20 to-transparent rounded-2xl"></div>
                <img
                  src="/media/vex-iq-program.jpeg"
                  alt="Students working with VEX IQ robotics equipment"
                  className="relative w-full h-auto object-cover rounded-2xl shadow-[0_20px_60px_rgba(0,0,0,0.6)]"
                />
              </motion.div>
            </div>
          </div>
        </section>

        {/* What Is VEX IQ Section */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-gray-100">
                  What Is VEX IQ?
                </h2>
                <p className="text-lg text-brand-400 font-medium">
                  Educational Robotics Platform for Young Learners
                </p>
              </div>

              <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  VEX IQ is an educational robotics platform developed by VEX
                  Robotics. It's designed for elementary and middle school
                  students, typically ages 8 to 14, to help them learn
                  engineering, programming, and teamwork in a fun, hands-on way.
                </p>
                <p>
                  The robots are made of snap-together plastic parts, which
                  makes them easy to assemble—even for beginners. Using motors,
                  sensors, wheels, gears, and more, students can design and
                  build their own functional robots and compete in official
                  robotics challenges.
                </p>
                <p>
                  The goal of VEX IQ is to teach STEM skills (Science,
                  Technology, Engineering, and Math) through creative
                  problem-solving, critical thinking, and collaboration.
                </p>
              </div>
            </div>

            <div className="bg-neutral-800/30 rounded-2xl p-6 border border-neutral-700">
              <h3 className="text-xl font-semibold text-gray-100 mb-4">
                Key Features
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Snap-together plastic parts - no tools required</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Perfect for ages 8-14</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Hands-on STEM learning</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Competitive robotics challenges</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Team-based learning approach</span>
                </li>
              </ul>
            </div>
          </div>
        </Section>

        {/* Building & Programming Section */}
        <Section>
          <div className="bg-neutral-800/30 rounded-2xl p-8 border border-neutral-700">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold tracking-tight mb-4 text-gray-100">
                How Do Students Build & Program Robots?
              </h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Accessible building system and intuitive programming for all
                skill levels
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-neutral-700/50 rounded-xl p-6 border border-neutral-600">
                <h3 className="text-xl font-semibold text-gray-100 mb-4">
                  Building System
                </h3>
                <p className="text-gray-300 mb-4">
                  One of the biggest advantages of VEX IQ is how accessible the
                  building system is. Unlike traditional robotics kits that
                  require screws or tools, VEX IQ parts snap together like
                  building blocks, which makes it perfect for younger students
                  or those new to robotics.
                </p>
                <h4 className="font-medium text-brand-400 mb-2">
                  Each VEX IQ kit contains:
                </h4>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Structural plastic parts</li>
                  <li>• Smart motors</li>
                  <li>• Wheels and gears</li>
                  <li>• A brain (robot controller)</li>
                  <li>• Sensors (distance, color, touch)</li>
                  <li>• A controller (joystick-style remote)</li>
                </ul>
              </div>

              <div className="bg-neutral-700/50 rounded-xl p-6 border border-neutral-600">
                <h3 className="text-xl font-semibold text-gray-100 mb-4">
                  Programming
                </h3>
                <p className="text-gray-300 mb-4">
                  Once the robot is built, it needs to be programmed—and VEX IQ
                  makes that easy, too. Students can use VEXcode, a free
                  programming software developed specifically for VEX robots.
                </p>
                <h4 className="font-medium text-brand-400 mb-2">
                  Programming options:
                </h4>
                <ul className="space-y-2 text-gray-300 text-sm">
                  <li>• Block-based coding (drag-and-drop visual blocks)</li>
                  <li>• Python (text-based coding for advanced users)</li>
                  <li>• Driving with a controller</li>
                  <li>• Autonomous routines</li>
                  <li>• Sensor response programming</li>
                </ul>
              </div>
            </div>
          </div>
        </Section>

        {/* Competitions Section */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-gray-100">
                  VEX IQ Competitions
                </h2>
                <p className="text-lg text-brand-400 font-medium">
                  Global Robotics Tournament Series
                </p>
              </div>

              <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  The VEX IQ Competition (VIQC) is a global robotics tournament
                  series where students form teams and compete with their robots
                  in a structured game. Each season has a new game with
                  different goals, rules, and field setups.
                </p>
                <p>
                  VEX IQ competitions include Teamwork Challenge, Driving Skills
                  Challenge, and Programming Skills Challenge. These events
                  focus on collaboration, strategic thinking, and robot design,
                  giving students a chance to apply what they've learned in a
                  real-world setting.
                </p>
                <p>
                  Competitions start at the local level, with opportunities to
                  advance to State, National, and even World Championship
                  events.
                </p>
              </div>
            </div>

            <div className="bg-neutral-800/30 rounded-2xl p-6 border border-neutral-700">
              <h3 className="text-xl font-semibold text-gray-100 mb-4">
                Competition Types
              </h3>
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-brand-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-brand-400 font-semibold text-sm">
                      1
                    </span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-100">
                      Teamwork Challenge
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Two teams work together to score points on the field.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-brand-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-brand-400 font-semibold text-sm">
                      2
                    </span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-100">
                      Driving Skills Challenge
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Students manually control their robot to complete
                      objectives.
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="w-8 h-8 bg-brand-600/20 rounded-lg flex items-center justify-center flex-shrink-0">
                    <span className="text-brand-400 font-semibold text-sm">
                      3
                    </span>
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-100">
                      Programming Skills Challenge
                    </h4>
                    <p className="text-gray-300 text-sm">
                      Robots run autonomous routines fully coded by students.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Section>

        {/* Skills & Benefits Section */}
        <Section>
          <div className="bg-neutral-800/30 rounded-2xl p-8 border border-neutral-700">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold tracking-tight mb-4 text-gray-100">
                What Skills Will My Child Learn?
              </h2>
              <p className="text-lg text-gray-300 max-w-3xl mx-auto">
                Valuable skills that extend far beyond robotics and into
                everyday life
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Engineering & Design
                </h3>
                <p className="text-gray-300 text-sm">
                  Learn to think like engineers through hands-on design and
                  problem-solving
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Coding & Programming
                </h3>
                <p className="text-gray-300 text-sm">
                  Develop logical thinking and programming skills from blocks to
                  Python
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Teamwork & Leadership
                </h3>
                <p className="text-gray-300 text-sm">
                  Build collaboration skills and learn to work effectively in
                  teams
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Time Management
                </h3>
                <p className="text-gray-300 text-sm">
                  Learn to plan, organize, and manage projects with deadlines
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Public Speaking
                </h3>
                <p className="text-gray-300 text-sm">
                  Develop presentation skills during judging and team
                  presentations
                </p>
              </div>

              <div className="text-center">
                <div className="w-16 h-16 bg-brand-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-8 h-8 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2">
                  Problem Solving
                </h3>
                <p className="text-gray-300 text-sm">
                  Enhance critical thinking and creative problem-solving
                  abilities
                </p>
              </div>
            </div>
          </div>
        </Section>

        {/* Engineering Notebook Section */}
        <Section>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4 text-gray-100">
                  Engineering Notebook
                </h2>
                <p className="text-lg text-brand-400 font-medium">
                  Documenting the Design Process
                </p>
              </div>

              <div className="space-y-4 text-gray-300 text-lg leading-relaxed">
                <p>
                  While not required, an{" "}
                  <strong className="text-brand-400">
                    engineering notebook
                  </strong>{" "}
                  is strongly encouraged for students participating in VEX IQ
                  competitions—especially if they want to compete for judged
                  awards.
                </p>
                <p>
                  The notebook includes robot design ideas and sketches,
                  programming plans, build iterations, team goals and
                  reflections. It helps students think like real engineers by
                  documenting their design process and learning how to
                  communicate their ideas clearly.
                </p>
              </div>
            </div>

            <div className="bg-neutral-800/30 rounded-2xl p-6 border border-neutral-700">
              <h3 className="text-xl font-semibold text-gray-100 mb-4">
                Notebook Contents
              </h3>
              <ul className="space-y-3 text-gray-300">
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Robot design ideas and sketches</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Programming plans and strategies</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Build iterations and improvements</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Team goals and reflections</span>
                </li>
                <li className="flex items-start space-x-2">
                  <span className="text-brand-400 mt-1">•</span>
                  <span>Competition strategies and outcomes</span>
                </li>
              </ul>
            </div>
          </div>
        </Section>

        {/* Getting Started Section */}
        <Section>
          <div className="bg-gradient-to-r from-neutral-800/50 to-neutral-700/50 rounded-2xl p-12 border border-neutral-600">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-bold tracking-tight mb-4 text-gray-100">
                How Can My Child Join the VEX IQ Program?
              </h2>
              <p className="text-xl text-gray-300 max-w-3xl mx-auto">
                Multiple pathways to get involved in competitive robotics
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8 mb-10">
              <div className="bg-neutral-700/30 rounded-xl p-6 border border-neutral-600">
                <div className="w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2 text-center">
                  School Teams
                </h3>
                <p className="text-gray-300 text-center text-sm">
                  Many schools offer VEX IQ as part of a STEM class or
                  after-school club.
                </p>
              </div>

              <div className="bg-neutral-700/30 rounded-xl p-6 border border-neutral-600">
                <div className="w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2 text-center">
                  Private Coaching
                </h3>
                <p className="text-gray-300 text-center text-sm">
                  Programs like Valkyrie Robotics offer competitive coaching
                  outside of the school system.
                </p>
              </div>

              <div className="bg-neutral-700/30 rounded-xl p-6 border border-neutral-600">
                <div className="w-12 h-12 bg-brand-600/20 rounded-lg flex items-center justify-center mx-auto mb-4">
                  <svg
                    className="w-6 h-6 text-brand-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-100 mb-2 text-center">
                  Community Teams
                </h3>
                <p className="text-gray-300 text-center text-sm">
                  Some libraries, homeschool groups, or community centers run
                  their own teams.
                </p>
              </div>
            </div>

            <div className="text-center">
              <motion.button
                data-cal-namespace="free-trial"
                data-cal-link="aplakosh/free-trial"
                data-cal-config='{"layout":"month_view"}'
                className="rounded-lg bg-brand-600 text-neutral-900 font-semibold px-10 py-4 text-xl transition-all duration-150 hover:scale-105 hover:bg-brand-500 active:scale-95 shadow-lg"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Book Free Trial Session
              </motion.button>
              <FreeTrialSchedule className="mt-4" />
              <p className="text-gray-400 mt-4">
                Questions about VEX IQ?{" "}
                <a href="/contact" className="text-brand-400 hover:underline">
                  Contact us
                </a>{" "}
                or call (470) 239-0787
              </p>
            </div>
          </div>
        </Section>
      </div>
    </>
  );
}
